import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import {
  FILTER_OPTION_KEYS,
  groupForCategory,
} from '@totem/components/services/threats/eventsDashboard/report/configuration';
import EventsReportContext from '@totem/components/services/threats/eventsDashboard/report/EventsReportContext';
import {
  DashboardMode,
  DashboardSummary,
  ReportTab,
} from '@totem/components/services/threats/eventsDashboard/report/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import {
  EVENTS_ENDPOINT,
  V2_EVENTS_ENDPOINT,
} from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

// Fills the processed counts when the API predates them, so the page still renders.
const withProcessed = (summary: DashboardSummary): DashboardSummary => ({
  ...summary,
  processed: summary.processed ?? { events: 0, unassigned: 0 },
  buildings: summary.buildings.map((building) => ({
    ...building,
    processed: building.processed ?? 0,
    processedTrend: building.processedTrend ?? [],
    openTrend: building.openTrend ?? [],
  })),
});

const EventsReportContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    handleFilter,
    setFilterOptions,
    getFilterValue,
    isReady,
  } = useContext(InputContext);

  const [summary, setSummary] = useState<DashboardSummary>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [category, setCategory] = useState<string | null>(null);

  const mode: DashboardMode =
    getFilterValue('mode') === 'resolved' ? 'resolved' : 'open';

  const buildReportParameters = (): Params => buildParameters();
  // Paging belongs to the findings tables, so the report itself only follows the filters.
  const reportKey = JSON.stringify(buildReportParameters().filters);

  useEffect(() => {
    fetch(`${EVENTS_ENDPOINT}/filterOptions`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (isNotNull(result)) {
          FILTER_OPTION_KEYS.forEach((key) => {
            if (isNotNull(result[key])) {
              setFilterOptions(
                key,
                result[key].map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
          });
        }
      });
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setLoading(true);

    fetch(`${V2_EVENTS_ENDPOINT}/dashboard/summary`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify(buildReportParameters()),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`${res.status}`);
        }
        return res.json();
      })
      .then((result: DashboardSummary) => setSummary(withProcessed(result)))
      .catch(() =>
        notification.error({
          title: 'Events',
          description: 'The events summary could not be loaded.',
        }),
      )
      .finally(() => setLoading(false));
  }, [reportKey, isReady, userProfile.organization.id]);

  // Opens the findings tab for a service area, filtered to it.
  const showCategory = (newCategory: string) => {
    setCategory(newCategory);

    setActiveTab(groupForCategory(newCategory));
  };

  const setMode = (newMode: DashboardMode) => {
    // Each view keeps its own time scale: open issues default to all time, resolved ones to the last 30 days.
    handleFilter('dateRange', []);

    handleFilter('mode', newMode);
  };

  return (
    <EventsReportContext.Provider
      value={{
        mode,
        setMode,
        summary,
        loading,
        activeTab,
        setActiveTab,
        category,
        showCategory,
        clearCategory: () => setCategory(null),
        buildReportParameters,
        reportKey,
      }}
    >
      {children}
    </EventsReportContext.Provider>
  );
};

export default EventsReportContainer;
